import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";
import Constants from "../../services/constants";
import { hideLoader, showLoader } from "./loaderSlice";
// import { showToast } from "./toastSlice";
import { getMyProfileAction, getMyRole } from "./userSlice";
import { setSubscribedPlan } from "./planSlice";

const initialState = {
  data: [],
  active: {},
  activePlan: {},
};

export const getOrgAction = createAsyncThunk(
  "org/get",
  async ({ data }, { dispatch }) => {
    try {
      const res = await api("get", Constants.END_POINT.organization, {});
      if (res?.success && res?.data) {
        dispatch(setOrgDetail(res?.data));
      } else {
        // dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      // dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    }
  }
);

export const getUserOrgAction = createAsyncThunk(
  "org/get",
  async (data, next, { dispatch }) => {
    try {
      const res = await api(
        "get",
        `${Constants.END_POINT.organization}${data}/orgs`,
        {}
      );
      if (res?.success && res?.data) {
        dispatch(setOrgDetail(res?.data));
        next(res.success);
      } else {
        // dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      // dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    }
  }
);

export const createOrgAction = createAsyncThunk(
  "org/create",
  async ({ data, next }, { dispatch }) => {
    try {
      dispatch(showLoader());

      const res = await api("post", Constants.END_POINT.organization, data);
      if (res?.success && res?.data) {
        await dispatch(getMyProfileAction({ navigate: () => null }));
        if (res.data?.id) {
          // Fetch organization with plan details
          const orgWithPlan = await api(
            "get",
            `${Constants.END_POINT.organization}${res.data.id}`
          );
          if (orgWithPlan?.success && orgWithPlan?.data) {
            dispatch(setActiveOrg(orgWithPlan.data));
            dispatch(
              setActiveOrgPlan(orgWithPlan?.data?.Subscriptions[0]?.Plan)
            );
            dispatch(setSubscribedPlan(orgWithPlan?.data?.Subscriptions[0]));
            // dispatch(showToast({ severity: "success", summary: res.message }));
            next(res.data.id);
            return Promise.resolve(orgWithPlan.data);
          }
        }
        throw new Error("Invalid organization data received");
      } else {
        throw new Error(res.message || "Failed to create organization");
      }
    } catch (error) {
      // dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject(error.message);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const updateOrganization = createAsyncThunk(
  "apiToken/post",
  async ({ data, next = () => null }, { dispatch }) => {
    try {
      dispatch(showLoader());
      const res = await api(
        "post",
        `${Constants.END_POINT.organization_update}${data?.id}`,
        data
      );
      if (res?.success && res?.data) {
        // dispatch(showToast({ severity: "success", summary: res.message }));
        // dispatch(getUserDetails());
        next();
      } else {
        // dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      // dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const deleteOrgAction = createAsyncThunk(
  "org/delete",
  async ({ id, next }, { dispatch }) => {
    let details = null;
    try {
      dispatch(showLoader());
      const res = await api("delete", Constants.END_POINT.organization + id);
      if (res?.success && res?.data) {
        dispatch(setActiveOrg({}));
        dispatch(getMyRole({}));
        details = res?.message;
        await dispatch(getMyProfileAction({ navigate: () => null }));
      } else {
        // Improved error message handling
        if (res?.data?.error === "has_resources") {
          details =
            "Cannot delete organization that contains resources. Please delete all projects, databases, edge functions, and other resources first.";
        } else if (res?.data?.error === "has_teams") {
          details =
            "Cannot delete organization that has team members. Please remove all team members first.";
        } else if (res?.data?.message) {
          details = res.data.message;
        } else {
          details =
            "Failed to delete organization. Please ensure all resources and team members are removed first.";
        }
        await dispatch(getMyProfileAction({ navigate: () => null }));
      }
    } catch (error) {
      // dispatch(showToast({ severity: "error", summary: error?.message }));
      return Promise.reject("An error occurred." || error?.message);
    } finally {
      dispatch(hideLoader());
      return Promise.resolve(details);
    }
  }
);

// New action to fetch organization with plan details
export const fetchOrgWithPlan = createAsyncThunk(
  "org/fetchWithPlan",
  async (orgId, { dispatch }) => {
    try {
      const res = await api(
        "get",
        `${Constants.END_POINT.organization}${orgId}`
      );
      if (res?.success && res?.data) {
        dispatch(setActiveOrg(res.data));
        return res.data;
      } else {
        throw new Error(res.message || "Failed to fetch organization details");
      }
    } catch (error) {
      // dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject(error.message);
    }
  }
);

const orgSlice = createSlice({
  name: "org",
  initialState,
  reducers: {
    setOrgDetail: (state, action) => {
      state.data = action.payload;
    },
    setActiveOrg: (state, action) => {
      state.active = action.payload;
    },
    setActiveOrgPlan: (state, action) => {
      state.activePlan = action.payload;
    },
    resetOrg: (state) => {
      return initialState;
    },
  },
});

export const { setOrgDetail, setActiveOrg, setActiveOrgPlan, resetOrg } =
  orgSlice.actions;

export default orgSlice.reducer;
